
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	Platform,
	SportEventAggregation,
	SportEventBucketsEdgeV2,
	SportEventStatus,
	SportEventV2,
	SportType,
} from '@/@types/graphql-types';
import { FilteredEvents, TimeGroup } from '@/components/sports/types';
import { isSameDate, shiftDateWithTimezone } from '@/helpers/date-helper';
import { LocaleResponse } from '@/helpers/locale-helper';
import { SportPageType } from '@/interfaces/sport-graphql';

import {
	GetSingleStepSportOverviewDocument,
	GetSingleStepSportOverviewQueryVariables,
} from '@/pages/graphql/queries/GetSingleStepSportOverview.query';

import { SingleStepContext } from '@/helpers/sports/config';

const Language = namespace('language');

@Component({
	name: 'ProvideUpcomingSportEvents',
	apollo: {
		upcomingEvents: {
			query: GetSingleStepSportOverviewDocument,
			variables(): GetSingleStepSportOverviewQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.sportEventBucketsV2.edges;
			},
			skip() {
				if (this.$route.name === 'app.home') return false;
				if (this.$route.name === 'app.sports.all') return false;

				if (this.$route.meta?.data == null) return true;

				return (
					!this.$route.meta.data.includes('singlestep') ||
					(this.context.pageType === SportPageType.SportCompetition && this.competitionId == null) ||
					(this.context.pageType === SportPageType.SportTeam && this.competitorId == null)
				);
			},
		},
	},
})
export default class ProvideUpcomingSportEvents extends Vue {
	upcomingEvents: SportEventBucketsEdgeV2[] = [] as SportEventBucketsEdgeV2[];

	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ default: null }) competitionId: string | null;
	@Prop({ default: null }) competitorId: string | null;

	@Language.Getter language: string;
	@Language.Getter country: string;
	@Language.Getter localeObject: LocaleResponse;

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				data: {
					timeGroup: this.timeGroup,
					events: this.timeGroupedEvents[this.timeGroup],
					isDataLoading: this.isDataLoading,
					context: this.context,
				},
				events: this.events,
				eventIs: {
					live: this.eventIsLive,
					today: this.eventIsToday,
					upcoming: this.eventIsUpcoming,
				},
			});
		}

		return this.$slots[0];
	}

	get isDataLoading() {
		return this.$apollo.queries.upcomingEvents.loading;
	}

	get timeGroup(): TimeGroup {
		if (this.timeGroupedEvents.live.length > 0) return 'live';
		return this.timeGroupedEvents.today.length > 0 ? 'today' : 'upcoming';
	}

	get localeToday() {
		return shiftDateWithTimezone(this.localeObject.timezone);
	}

	get queryVariables(): GetSingleStepSportOverviewQueryVariables {
		const variables = {
			platform: Platform.Web,
			language: this.language,
			country: this.country,
			first: 1,
			bucketSize: 10,
			groupBy: SportEventAggregation.Sport,
			filter: {
				startAt: { from: this.localeToday.toISOString() },
				competitionIds: [] as string[],
				competitorIds: [] as string[],
				sports: [] as SportType[],
			},
			timezoneOffset: 0,
		};

		if (this.context.pageType === SportPageType.SportCompetition) {
			variables.filter.competitionIds = [this.competitionId!];
		}
		if (this.context.pageType === SportPageType.SportTeam) {
			variables.filter.competitorIds = [this.competitorId!];
		} else {
			variables.filter.sports = [this.context.sportType];
		}

		return variables;
	}

	get timeGroupedEvents(): FilteredEvents {
		const filteredEvents: FilteredEvents = { live: [], today: [], upcoming: [] };

		this.events.forEach(event => filteredEvents[this.getEventGroup(event)].push(event));

		return filteredEvents;
	}

	get events() {
		return this.upcomingEvents
			.map(({ node }) => node.edges ?? [])
			.flat()
			.map(({ node }) => node);
	}

	getEventGroup(event: SportEventV2): TimeGroup {
		const startAt = new Date(event.content.startAt);
		const localStartAt = shiftDateWithTimezone(this.localeObject.timezone, 0, startAt);

		if (event.content.status === SportEventStatus.Live) return 'live';

		return isSameDate(this.localeToday, localStartAt) ? 'today' : 'upcoming';
	}

	eventIsLive = (event: SportEventV2) => this.getEventGroup(event) === 'live';
	eventIsToday = (event: SportEventV2) => this.getEventGroup(event) === 'today';
	eventIsUpcoming = (event: SportEventV2) => this.getEventGroup(event) === 'upcoming';

	mounted() {
		if (this.context.pageType === SportPageType.SportCompetition && this.competitionId == null) {
			throw Error('[ProvideUpcomingSingleStepEvent] "competitionId" prop is required on competition pages.');
		}

		if (this.context.pageType === SportPageType.SportTeam && this.competitorId == null) {
			throw Error('[ProvideUpcomingSingleStepEvent] "competitorId" prop is required on team pages.');
		}
	}
}
